// session.ts — M0 SessionStore (Zustand) per FE_SPEC state_stores.md.
//
// Tracks the current council session FSM, persona speech buffers, iron kills,
// final report, error envelope, and SSE last_event_id for resume.
//
// Wire convention: snake_case fields match backend openapi.fyd.v1.yaml DTO shape.
// PascalCase for top-level types per TypeScript convention.
import { create } from 'zustand';
import type { IntentVector } from '@/types/intent';
import type { TranscriptEvent } from '@/types/transcript';
import { mergeTranscriptIntoStore } from '@/lib/transcript_merge';
import type {
  ChoreographyDTO,
  IntentInferredDTO,
  AttachmentBriefingDTO,
  SceneSymbol,
  GatingMode,
  SessionCreateResponse,
} from '@/api/m0_sessions';

export type SessionFsmState =
  | 'init'
  | 'payment_pending'
  | 'payment_confirmed'
  | 'running_p1'
  | 'running_p2'
  | 'running_p3'
  | 'running_p4'
  | 'running_p5'
  | 'running_p6'
  | 'iron_silent_review'
  | 'completed'
  | 'aborted';

// ★ a7 P1 fix: 'sage_synthesis' (NOT 'synthesis') — catches the a7 hallucination
// at compile time. The 13-entry list is the M0.5 superset spanning legacy
// 5-persona councils (sage/hammer/compass/horizon/iron + sage_synthesis) and
// the 7 RoleIDs introduced for IntentVector-routed councils.
export type PersonaId =
  | 'sage'
  | 'sage_synthesis'
  | 'iron'
  | 'hammer'
  | 'compass'
  | 'horizon'
  | 'architect'
  | 'scribe'
  | 'steward'
  | 'mediator'
  | 'mirror'
  | 'empath'
  | 'ideator';

// Insertion order is preserved by both V8 and SpiderMonkey for string keys —
// iterating `Object.entries(personas)` yields this exact order, which the
// PersonaCard grid relies on for stable layout.
export const ALL_PERSONAS: PersonaId[] = [
  'sage',
  'sage_synthesis',
  'iron',
  'hammer',
  'compass',
  'horizon',
  'architect',
  'scribe',
  'steward',
  'mediator',
  'mirror',
  'empath',
  'ideator',
];

export type PhaseId =
  | 'intake'
  | 'framing'
  | 'exploring'
  | 'narrowing'
  | 'proposing'
  | 'confirming'
  | 'resolving';

export interface IronKill {
  candidate_id: string;
  /** Wire shape per owner_loop.md §4 v2 (was kill_reason in pre-R2). */
  reason: string;
  monotonic_id: number;
}

export interface PersonaSpeechBuffer {
  persona_id: PersonaId;
  phase: PhaseId | null;
  chunks: string[];
  complete: boolean;
  monotonic_id_last: number;
  /**
   * Monotonic SSE event id of the FIRST speech_chunk this persona emitted in the
   * current phase. 0 = persona has not spoken yet. Used by Live.tsx to render
   * PersonaCards in spoken-order (so the user sees who spoke 1st, 2nd, …)
   * instead of canonical alphabetical order.
   * Reset to 0 on phase_started (each phase has fresh ordering).
   */
  monotonic_id_first: number;
}

export interface ErrorEnvelope {
  code: string;
  message: { i18n_key: string; default_text_zh: string; default_text_en: string };
  trace_id: string;
}

export interface SessionDraft {
  topic: string;
  role: string | null;
  stage: string | null;
  time_budget_h_per_week: number | null;
  material_ids: string[];
}

// ★ M2.1: linear chat-style message log · 用户反馈 "议会改聊天对话线性形式"
// 每个 speech_chunk 累积进同 persona 当前活跃 message · is_final=true 后锁定 ·
// 下次同 persona 再说话起新 message。phase_summary / iron_kill 作 system 消息
// inline 插入。Live.tsx 按 chronological 顺序渲染 + auto-scroll 到底。
export interface ChatMessage {
  /** 单调递增 · 用 first speech_chunk 的 event.id · 或合成 (system 消息用 phase_started id) */
  id: number;
  /** chronological 排序基准 (相同 phase 内同一 persona 第一条 chunk 的 event.id) */
  monotonic_id: number;
  kind: 'speech' | 'iron_kill' | 'phase_summary' | 'phase_marker';
  /** 'speech' 时为 PersonaId · 否则 null (system 消息无 author) */
  author: PersonaId | null;
  /** speech 累积内容 · system 消息为单段 markdown */
  body: string;
  /** speech 时 is_final · system 消息总是 true */
  complete: boolean;
  /** 当前 phase name · 用于在 UI 上标 phase 上下文 */
  phase: string | null;
  /** ISO timestamp (FE 收到 chunk 时 · 仅展示用 · 非权威) */
  ts: string;
}

interface SessionCurrent {
  session_id: string | null;
  status: SessionFsmState;
  // ★ M2 widening: phase name now ∈ 12 closed-set string OR legacy PhaseId.
  // Stored as union for back-compat; consumers should treat as `string | null`
  // and use PhaseRouter switch for routing (FE_SPEC §9.2 / §12.1).
  current_phase: PhaseId | string | null;
  personas: Record<PersonaId, PersonaSpeechBuffer>;
  /**
   * ★ M2.1: chronological chat log · user-driven UX shift
   * (Live.tsx renders this linearly · ChatGPT-style)
   */
  messages: ChatMessage[];
  iron_kills: IronKill[];
  v9_status: 'pending' | 'pass' | 'warning' | 'fail' | null;
  v9_warning: string | null;
  // ★ M2 widening: 12 phase-name keyed map. Use Record<string,string> so M0/M2
  // phases coexist (FE_SPEC §12.3 phase_paused writes by data.phase_name).
  phase_summaries: Record<string, string>;
  report: unknown | null; // types.Report from backend; opaque to store
  error: ErrorEnvelope | null;
  last_event_id: number;
  started_at: string | null;
  completed_at: string | null;
  sse_url: string | null;
  // ★ M0.5 — null for legacy sessions or before BE replies. FE renders no
  // glyph when null; PersonaCard falls back to paper-pearl palette for
  // unknown RoleIDs to keep forward-compat (BE may add a 14th).
  intent_vector: IntentVector | null;
  // ── ★ M2 NEW (FE_SPEC §12.1) ────────────────────────────────────────────
  /** Composer-emitted phase choreography (POST /sessions 200). */
  choreography: ChoreographyDTO | null;
  /** Sage 推断的 intent_vector + scene_symbols 全套. */
  intent_inferred: IntentInferredDTO | null;
  /** Sage briefing summary · null when no attachments / not yet computed. */
  attachment_briefing: AttachmentBriefingDTO | null;
  /** Top-level scene_symbols copy for IntentBadge convenience. */
  scene_symbols: SceneSymbol[];
  /** 'auto' | 'user_advance' — Composer 决定 · null 时走默认 'auto'. */
  gating_mode: GatingMode | null;
  /** phase_paused 后 true · phase_advanced/phase_started 后 false (P0-11). */
  awaiting_advance: boolean;
  /**
   * ★ FIX-C3: phase_paused payload `phase_index` 写入 · sessionAdvance(sessionId, phaseIdx) 来源。
   * phase_started/phase_advanced 重置为 null (已离开 paused phase).
   */
  current_phase_idx: number | null;
  /** 503 fallback path 使用静态 choreography (P0-12). */
  fallback_used: boolean;
  /** composer_unavailable / composer_json_invalid / composer_invariant_violation / composer_timeout / fallback_invalid. */
  fallback_reason: string | null;
  // ── ★ DeepSeek audit pass 4-6 fixes (FE_SPEC §12.1) ─────────────────────
  /** ★ phase_paused payload: where to POST advance (FE_SPEC §13.3 fix C7). */
  advance_endpoint: string | null;
  /** ★ phase_paused gate: BE may set false if advance not yet allowed. */
  can_advance: boolean;
  /** Total phases (from phase_paused payload). */
  phases_total: number | null;
  /** Phases completed so far. */
  phases_completed: number | null;
  /** confirmation:report_ready payload.report_id capture (pass-3 F-003). */
  report_id: string | null;
  /** confirmation:session_refunded payload.reason / synthetic abort. */
  abort_reason: string | null;
  /**
   * ★ phase_paused awaiting_advance owner — phase index whose advance we
   * are waiting for; cleared on phase_advanced (pass-4 F-001).
   */
  awaiting_advance_phase_idx: number | null;
}

const emptyPersona = (id: PersonaId): PersonaSpeechBuffer => ({
  persona_id: id,
  phase: null,
  chunks: [],
  complete: false,
  monotonic_id_last: 0,
  monotonic_id_first: 0,
});

// Initialize all 13 persona entries up-front so iteration is deterministic
// and PersonaCard never has to handle "missing key" branches.
const emptyPersonas = (): Record<PersonaId, PersonaSpeechBuffer> =>
  ALL_PERSONAS.reduce(
    (acc, id) => {
      acc[id] = emptyPersona(id);
      return acc;
    },
    {} as Record<PersonaId, PersonaSpeechBuffer>,
  );

const blankCurrent = (): SessionCurrent => ({
  session_id: null,
  status: 'init',
  current_phase: null,
  personas: emptyPersonas(),
  messages: [],
  iron_kills: [],
  v9_status: null,
  v9_warning: null,
  phase_summaries: {},
  report: null,
  error: null,
  last_event_id: 0,
  started_at: null,
  completed_at: null,
  sse_url: null,
  intent_vector: null,
  // ── M2 defaults ──
  choreography: null,
  intent_inferred: null,
  attachment_briefing: null,
  scene_symbols: [],
  gating_mode: null,
  awaiting_advance: false,
  current_phase_idx: null,
  fallback_used: false,
  fallback_reason: null,
  // ── DeepSeek audit pass 4-6 (M2) ──
  advance_endpoint: null,
  can_advance: false,
  phases_total: null,
  phases_completed: null,
  report_id: null,
  abort_reason: null,
  awaiting_advance_phase_idx: null,
});

const blankDraft = (): SessionDraft => ({
  topic: '',
  role: null,
  stage: null,
  time_budget_h_per_week: null,
  material_ids: [],
});

interface SessionStore {
  current: SessionCurrent;
  draft: SessionDraft;

  setDraft: (patch: Partial<SessionDraft>) => void;
  resetDraft: () => void;
  initSession: (sessionId: string, sseUrl: string) => void;
  // Pre-iter_3 dispatch shim: data is `any` because per-type union of SSE event
  // payloads is governed by m0 openapi schema (sse_event), not by FE types
  // directly — exhaustive narrowing happens inside the reducer below.
  // ★ TODO (FE_SPEC §11.1 + §12 · DeepSeek pass-1 fix C1): replace with a
  //    discriminated SSEEventEnvelope union. Out of scope for Phase 4c slice;
  //    20+ test sites + 4 reducer cases need to be updated atomically.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applySSEEvent: (event: { type: string; id: number; data: any }) => void;
  // M0.5 (iter_7) — replay persisted transcript before SSE subscribes (Bug B).
  // Idempotent: events with event_id ≤ current.last_event_id are skipped.
  // Returns count of events actually applied.
  mergeTranscript: (events: readonly TranscriptEvent[]) => number;
  appendIronKill: (kill: IronKill) => void;
  setReport: (report: unknown) => void;
  setError: (e: ErrorEnvelope) => void;
  resetCurrent: () => void;
  // ── ★ M2 NEW (FE_SPEC §12.4) ──
  initFromSessionCreateResponse: (resp: SessionCreateResponse) => void;
  setAwaitingAdvance: (v: boolean) => void;
  setFallback: (used: boolean, reason: string | null) => void;
}

export const useSessionStore = create<SessionStore>((set, get) => ({
  current: blankCurrent(),
  draft: blankDraft(),

  setDraft: (patch) => set((s) => ({ draft: { ...s.draft, ...patch } })),
  resetDraft: () => set({ draft: blankDraft() }),

  initSession: (sessionId, sseUrl) =>
    set((s) => ({
      current: { ...blankCurrent(), session_id: sessionId, sse_url: sseUrl, status: 'init', started_at: new Date().toISOString() },
      draft: s.draft,
    })),

  applySSEEvent: (event) =>
    set((s) => {
      // FYD-2026-0512: idempotency guard. SSE reconnect (Last-Event-ID) makes
      // BE Sink.ReplayFromID re-emit events from the ringBuffer; without this
      // skip, applySSEEvent appends duplicate ChatMessages / iron_kills /
      // etc. (mergeTranscript already has the same guard · line 293).
      // event.id = 0 (early bootstrap events without IDs) deliberately bypasses
      // the skip so legitimate uninstrumented paths still flow through.
      if (event.id > 0 && event.id <= s.current.last_event_id) {
        return s;
      }
      const cur = { ...s.current, last_event_id: Math.max(s.current.last_event_id, event.id) };
      switch (event.type) {
        case 'phase_started': {
          cur.current_phase = event.data.phase_name;
          cur.status = phaseToStatus(event.data.phase_name);
          // ★ M2 P0-11: new phase started → user已 advance · 重置等待状态.
          cur.awaiting_advance = false;
          cur.current_phase_idx = null;
          // Reset per-phase spoken-order: each phase has its own 1st/2nd/3rd
          // speaker ranking (sage-led framing → ideator+compass exploration etc).
          // Live.tsx sorts PersonaCards by monotonic_id_first within current
          // phase; clearing here makes prior-phase order irrelevant to next.
          cur.personas = Object.fromEntries(
            Object.entries(cur.personas).map(([pid, buf]) => [
              pid,
              { ...buf, monotonic_id_first: 0 },
            ]),
          ) as Record<PersonaId, PersonaSpeechBuffer>;
          // ★ M2.1 chat-style: insert phase_marker system message
          const speakers = Array.isArray(event.data.speakers)
            ? (event.data.speakers as string[])
            : [];
          const markerMsg: ChatMessage = {
            id: event.id,
            monotonic_id: event.id,
            kind: 'phase_marker',
            author: null,
            body: `▸ ${event.data.phase_name}${speakers.length ? ` · ${speakers.join(' · ')}` : ''}`,
            complete: true,
            phase: event.data.phase_name as string,
            ts: new Date().toISOString(),
          };
          cur.messages = [...cur.messages, markerMsg];
          break;
        }
        case 'phase_paused': {
          // ★ M2 (FE_SPEC §12.3): user_advance gating · 30min fallthrough.
          // ★ DeepSeek pass-4 F-001 + pass-5/6 fixes: capture advance_endpoint,
          // can_advance, phases_total/completed; defensive idx ≥ 0 check.
          cur.awaiting_advance = true;
          if (typeof event.data.phase_index === 'number' && event.data.phase_index >= 0) {
            cur.current_phase_idx = event.data.phase_index;
            cur.awaiting_advance_phase_idx = event.data.phase_index;
          }
          // ★ pass-4 F-002: also persist current_phase = phase_name during pause.
          if (typeof event.data.phase_name === 'string') {
            cur.current_phase = event.data.phase_name;
            if (typeof event.data.phase_summary === 'string') {
              cur.phase_summaries = {
                ...cur.phase_summaries,
                [event.data.phase_name]: event.data.phase_summary,
              };
            }
          }
          // ★ pass-5 fix C7: capture advance_endpoint + can_advance from payload.
          if (typeof event.data.advance_endpoint === 'string') {
            cur.advance_endpoint = event.data.advance_endpoint;
          }
          if (typeof event.data.can_advance === 'boolean') {
            cur.can_advance = event.data.can_advance;
          } else {
            // BE may omit can_advance; default to true when paused
            cur.can_advance = true;
          }
          if (typeof event.data.phases_total === 'number') {
            cur.phases_total = event.data.phases_total;
          }
          if (typeof event.data.phases_completed === 'number') {
            cur.phases_completed = event.data.phases_completed;
          }
          break;
        }
        case 'phase_advanced': {
          // ★ M2 P0-11 闭环 (FE_SPEC §12.3 · FIX-C1): force-reset 不论之前状态。
          // payload 字段对齐 BE_SPEC §10.1: { from_phase, to_phase, reason, advanced_at }
          // ★ pass-4 F-001: also clear awaiting_advance_phase_idx + advance_endpoint + can_advance.
          cur.awaiting_advance = false;
          cur.awaiting_advance_phase_idx = null;
          cur.advance_endpoint = null;
          cur.can_advance = false;
          if (typeof event.data.to_phase === 'string') {
            // store invariant: 收到 phase_advanced 时若当前 phase 不是 from_phase · 上报 skew 但仍 force-reset.
            if (
              typeof event.data.from_phase === 'string' &&
              typeof cur.current_phase === 'string' &&
              cur.current_phase !== event.data.from_phase
            ) {
              console.warn('phase_advanced_skew', {
                expected: event.data.from_phase,
                got: cur.current_phase,
              });
            }
            cur.current_phase = event.data.to_phase;
          }
          // ★ FIX-C3: 已离开 paused phase · 重置 idx.
          cur.current_phase_idx = null;
          break;
        }
        case 'speech_chunk': {
          const pid = event.data.persona_id as PersonaId;
          const buf = cur.personas[pid] ?? emptyPersona(pid);
          const wasComplete = buf.complete;
          const nowComplete = wasComplete || Boolean(event.data.is_final);
          cur.personas = {
            ...cur.personas,
            [pid]: {
              ...buf,
              phase: cur.current_phase,
              chunks: [...buf.chunks, event.data.delta_text],
              // Once a buffer has gone complete it stays complete (M1 streaming forward-compat).
              complete: nowComplete,
              monotonic_id_last: event.id,
              // Capture the FIRST speech_chunk monotonic_id so Live.tsx can
              // render PersonaCards in spoken-order (1st / 2nd / 3rd … speaker)
              // instead of canonical alphabetical order. Phase boundary
              // resets via phase_started.
              monotonic_id_first: buf.monotonic_id_first || event.id,
            },
          };
          // ★ M2.1 chat-style: append OR update last open ChatMessage for this
          // persona within current phase. Same persona keeps appending until
          // is_final flips · then next chunk starts new message.
          {
            const lastIdx = cur.messages.length - 1;
            const last = lastIdx >= 0 ? cur.messages[lastIdx] : null;
            const sameOpenMessage =
              last &&
              last.kind === 'speech' &&
              last.author === pid &&
              last.phase === cur.current_phase &&
              !last.complete;
            const deltaText = (event.data.delta_text as string) ?? '';
            if (sameOpenMessage) {
              const updated: ChatMessage = {
                ...last,
                body: last.body + deltaText,
                complete: nowComplete,
              };
              cur.messages = [...cur.messages.slice(0, lastIdx), updated];
            } else {
              const newMsg: ChatMessage = {
                id: event.id,
                monotonic_id: event.id,
                kind: 'speech',
                author: pid,
                body: deltaText,
                complete: nowComplete,
                phase: cur.current_phase,
                ts: new Date().toISOString(),
              };
              cur.messages = [...cur.messages, newMsg];
            }
          }
          break;
        }
        case 'iron_kill': {
          // ★ pass-5 F-002: BE only emits {candidate_id, reason}. NO cited_evidence on wire.
          cur.iron_kills = [
            ...cur.iron_kills,
            {
              candidate_id: event.data.candidate_id,
              reason: event.data.reason ?? event.data.kill_reason ?? '',
              monotonic_id: event.id,
            },
          ];
          // ★ M2.1 chat-style: surface kill as inline system message
          const killMsg: ChatMessage = {
            id: event.id,
            monotonic_id: event.id,
            kind: 'iron_kill',
            author: null,
            body: `[KILL ${event.data.candidate_id}] ${event.data.reason ?? event.data.kill_reason ?? ''}`,
            complete: true,
            phase: cur.current_phase,
            ts: new Date().toISOString(),
          };
          cur.messages = [...cur.messages, killMsg];
          break;
        }
        case 'v9_check':
          cur.v9_status = event.data.warning ? 'warning' : event.data.pass ? 'pass' : 'fail';
          cur.v9_warning = typeof event.data.warning === 'string' ? event.data.warning : null;
          break;
        case 'phase_summary': {
          // ★ M2 widening: phase_name 可以是 12 闭集 string · 不再限于 PhaseId.
          const ph = event.data.phase_name as string | undefined;
          if (ph) {
            const summaryText = event.data.summary_text ?? '';
            cur.phase_summaries = {
              ...cur.phase_summaries,
              [ph]: summaryText,
            };
            const summaryMsg: ChatMessage = {
              id: event.id,
              monotonic_id: event.id,
              kind: 'phase_summary',
              author: null,
              body: summaryText,
              complete: true,
              phase: ph,
              ts: new Date().toISOString(),
            };
            cur.messages = [...cur.messages, summaryMsg];
          }
          break;
        }
        case 'confirmation':
          if (event.data.action === 'report_ready') {
            cur.status = 'completed';
            cur.completed_at = new Date().toISOString();
            // ★ pass-3 F-003: capture report_id from payload for navigation.
            const payload = (event.data.payload ?? {}) as { report_id?: string };
            if (typeof payload.report_id === 'string') {
              cur.report_id = payload.report_id;
            }
          } else if (event.data.action === 'session_refunded') {
            cur.status = 'aborted';
            cur.completed_at = new Date().toISOString();
            // ★ pass-2 F-005: clear awaiting_advance + capture abort_reason.
            cur.awaiting_advance = false;
            cur.awaiting_advance_phase_idx = null;
            const refundPayload = (event.data.payload ?? event.data) as { reason?: string };
            if (typeof refundPayload.reason === 'string') {
              cur.abort_reason = refundPayload.reason;
            }
          }
          break;
        case 'briefing_ready': {
          // ★ M2.1 Wave 7 ARC-8 (sessions_m2.go:531 runAsyncScribe):
          // POST /sessions returns immediately with attachment_briefing.available=false
          // placeholder · async Scribe goroutine emits briefing_ready when done with
          // final payload. Payload schema from BE:
          //   { available, key_facts_count, key_data_count, contradictions_count,
          //     gaps_count, char_count, quality_compromised, elapsed_ms }
          //
          // Merge semantics:
          //   - available=true: overwrite placeholder with final briefing.
          //   - available=false (error path): keep existing non-null briefing if any
          //     (idempotent on success path); only set if no prior briefing.
          const payload = (event.data ?? {}) as Record<string, unknown>;
          const incomingAvailable = payload.available === true;
          const prev = cur.attachment_briefing;
          // If success: always overwrite (final wins · idempotent for same success payload).
          // If failure: only set if no prior available=true briefing recorded.
          if (incomingAvailable || !prev || prev.available !== true) {
            const next: AttachmentBriefingDTO = {
              available: incomingAvailable,
              key_facts_count: typeof payload.key_facts_count === 'number' ? payload.key_facts_count : 0,
              key_data_count: typeof payload.key_data_count === 'number' ? payload.key_data_count : 0,
              contradictions_count: typeof payload.contradictions_count === 'number' ? payload.contradictions_count : 0,
              gaps_count: typeof payload.gaps_count === 'number' ? payload.gaps_count : 0,
              char_count: typeof payload.char_count === 'number' ? payload.char_count : 0,
              raw_path: typeof payload.raw_path === 'string' ? payload.raw_path : (prev?.raw_path ?? ''),
              quality_compromised: payload.quality_compromised === true,
              content: typeof payload.content === 'string' ? payload.content : (prev?.content ?? null),
            };
            cur.attachment_briefing = next;
          }
          break;
        }
        case 'close': {
          // ★ FYD-2026-0515: BE emits `event: close` either when Owner
          // finishes (reason='owner_finished') or when a reconnect lands on
          // a session whose sink was already GC'd (reason='already_completed'
          // | 'already_aborted', see writeTerminalCloseStream in
          // internal/api/sse_handler.go). Treat it as the authoritative
          // terminal signal so Live.tsx's auto-redirect effect fires reliably
          // even when the preceding 'confirmation:report_ready' was missed
          // (race during reconnect / sink GC). Don't overwrite an already-
          // captured 'aborted' status — abort_reason banner stays accurate.
          const reason = (event.data as { reason?: string }).reason;
          if (cur.status !== 'aborted') {
            if (reason === 'already_aborted') {
              cur.status = 'aborted';
              const abortPayload = event.data as { abort_reason?: string };
              if (typeof abortPayload.abort_reason === 'string') {
                cur.abort_reason = abortPayload.abort_reason;
              }
            } else {
              cur.status = 'completed';
            }
            if (!cur.completed_at) {
              cur.completed_at = new Date().toISOString();
            }
          }
          break;
        }
        case 'error': {
          // iter_7 Bug C fix: owner emits {code, message, trace_id} (canonical)
          // or legacy {error_code, ...}. Read both.
          const resolvedCode: string | undefined =
            (typeof event.data.code === 'string' && event.data.code) ||
            (typeof event.data.error_code === 'string' && event.data.error_code) ||
            undefined;
          // ★ pass-2 F-005: if status flipped to aborted, clear awaiting_advance.
          if (resolvedCode && (resolvedCode === 'session_terminated' || resolvedCode === 'session_deadline')) {
            cur.awaiting_advance = false;
            cur.awaiting_advance_phase_idx = null;
            cur.advance_endpoint = null;
            cur.can_advance = false;
          }
          // ★ Fixed: surface malformed_sse_event when BE emits envelope without
          // a code. Pre-iter_7 transcript JSONL is rare in M2 (BE always emits
          // canonical envelopes); FE store accurately surfaces the gap rather
          // than silently swallow it. Live.tsx routes this to a calm
          // "malformed_event" copy (live.malformed_event) — not a panic.
          cur.error = {
            code: resolvedCode ?? 'malformed_sse_event',
            message: event.data.message,
            trace_id: event.data.trace_id ?? '',
          };
          break;
        }
      }
      return { current: cur };
    }),

  // M0.5 (iter_7) — Bug B fix · transcript replay on /live mount.
  // Delegates to the pure mergeTranscriptIntoStore helper. We pass a thin
  // proxy that reads last_event_id lazily (so applySSEEvent's updates inside
  // the loop are visible) and calls applySSEEvent directly on the store.
  mergeTranscript: (events: readonly TranscriptEvent[]): number => {
    const target = {
      get current() {
        return { last_event_id: get().current.last_event_id };
      },
      applySSEEvent: get().applySSEEvent,
    };
    return mergeTranscriptIntoStore(target, events);
  },

  appendIronKill: (kill) =>
    set((s) => ({ current: { ...s.current, iron_kills: [...s.current.iron_kills, kill] } })),

  setReport: (report) =>
    set((s) => ({ current: { ...s.current, report, status: 'completed' } })),

  setError: (e) => set((s) => ({ current: { ...s.current, error: e } })),

  resetCurrent: () => set({ current: blankCurrent() }),

  // ── ★ M2 actions (FE_SPEC §12.4) ──────────────────────────────────────
  initFromSessionCreateResponse: (resp) =>
    set((s) => {
      const startedAt = s.current.started_at ?? new Date().toISOString();
      return {
        current: {
          ...blankCurrent(),
          session_id: resp.session_id,
          sse_url: resp.sse_url,
          status: 'init',
          started_at: startedAt,
          // ★ pass-4 F-004: drop legacy resp.intent_vector fallback — M2 BE
          // emits intent_inferred only. Sessions arriving without it are pre-M2.
          intent_vector: resp.intent_inferred?.intent_vector ?? null,
          // M2 fields
          choreography: resp.choreography ?? null,
          intent_inferred: resp.intent_inferred ?? null,
          attachment_briefing: resp.attachment_briefing ?? null,
          scene_symbols: resp.intent_inferred?.scene_symbols ?? [],
          gating_mode: resp.choreography?.gating_mode ?? null,
          fallback_used: resp.fallback_used ?? false,
          fallback_reason: resp.fallback_reason ?? null,
        },
      };
    }),

  setAwaitingAdvance: (v) =>
    set((s) => ({ current: { ...s.current, awaiting_advance: v } })),

  setFallback: (used, reason) =>
    set((s) => ({ current: { ...s.current, fallback_used: used, fallback_reason: reason } })),
}));

function phaseToStatus(phase: string): SessionFsmState {
  switch (phase) {
    case 'framing': return 'running_p1';
    case 'exploration': return 'running_p2';
    case 'exploring': return 'running_p2';
    case 'narrowing': return 'running_p3';
    case 'challenging': return 'running_p4';
    case 'proposing': return 'running_p4';
    case 'validation': return 'running_p5';
    case 'confirming': return 'running_p5';
    case 'synthesis': return 'running_p6';
    case 'resolving': return 'running_p6';
    case 'listening':
    case 'reflection':
    case 'decomposition':
    case 'recomposition':
    case 'stress_testing':
    case 'mediation':
      return 'running_p2';
    default: return 'init';
  }
}

// Selectors
export const selectIsRunning = (s: SessionStore) =>
  s.current.status?.startsWith('running_') ?? false;

export const selectIsTerminal = (s: SessionStore) =>
  s.current.status === 'completed' || s.current.status === 'aborted';

export const selectIronKilledCount = (s: SessionStore) => s.current.iron_kills.length;
